// Frontend bridge for the backend normalized status model.
// Prefer API-provided `status_model`; keep fallback mappings so older API
// responses and local test fixtures remain compatible.

const VERIFICATION_PENDING = new Set(['pending', 'opened', 'in_progress', 'processing']);
const VERIFICATION_FAILED = new Set(['rejected', 'revoked', 'expired', 'failed']);
const DOCUMENT_ACTION_REQUIRED = new Set(['draft', 'setup_required', 'ready_to_send', 'action_required']);
const DOCUMENT_PENDING = new Set([
  'sent',
  'opened',
  'signing_started',
  'pending_verification',
  'ready_to_sign',
  'verification_started',
  'processing',
  'awaiting_signature',
  'signer_pending',
  'invitation_sent',
]);
const DOCUMENT_FAILED = new Set(['expired', 'revoked', 'failed']);

export function getStatusModel(item) {
  return item?.status_model || null;
}

export function getBucketStatus(item, fallbackKind = 'verification') {
  const model = getStatusModel(item);
  if (model?.bucket_status) return model.bucket_status;
  const status = item?.status || 'pending';
  if (fallbackKind === 'document') {
    if (['signed', 'completed'].includes(status)) return 'completed';
    if (DOCUMENT_FAILED.has(status)) return 'failed';
    if (DOCUMENT_ACTION_REQUIRED.has(status) || !item?.setup_complete) return 'action_required';
    if (DOCUMENT_PENDING.has(status)) return 'pending';
    return 'pending';
  }
  if (status === 'approved') return 'completed';
  if (status === 'needs_review') return 'action_required';
  if (VERIFICATION_FAILED.has(status)) return 'failed';
  if (VERIFICATION_PENDING.has(status)) return 'pending';
  return 'pending';
}

export function getDisplayStatus(item) {
  return getStatusModel(item)?.display_status || item?.display_status || String(item?.status || 'pending').replaceAll('_', ' ');
}

export function getListStatus(item, fallbackKind = 'verification') {
  const model = getStatusModel(item);
  if (model?.list_status) return model.list_status;
  const bucket = getBucketStatus(item, fallbackKind);
  if (fallbackKind === 'document') {
    if (bucket === 'completed') return 'signed';
    if (bucket === 'action_required') return 'action_required';
    if (bucket === 'pending') return 'pending';
    if (item?.status === 'revoked' || item?.status === 'expired') return item.status;
    return 'failed';
  }
  return item?.status || 'pending';
}

export function matchesBucket(item, filter, fallbackKind = 'verification') {
  if (!filter || filter === 'all') return true;
  if (filter === 'approved') return item?.status === 'approved';
  if (filter === 'signed') return ['signed', 'completed'].includes(item?.status);
  if (filter === 'needs_review') return getBucketStatus(item, fallbackKind) === 'action_required';
  if (filter === 'action_required') return getBucketStatus(item, fallbackKind) === 'action_required';
  if (filter === 'pending') return getBucketStatus(item, fallbackKind) === 'pending';
  if (filter === 'failed') return getBucketStatus(item, fallbackKind) === 'failed';
  if (filter === 'completed') return getBucketStatus(item, fallbackKind) === 'completed';
  return item?.status === filter;
}

export function closedAt(item, fallbackKind = 'verification') {
  const bucket = getBucketStatus(item, fallbackKind);
  if (!['completed', 'failed'].includes(bucket)) return null;
  if (fallbackKind === 'document') {
    return item?.signed_at || item?.usage_used_at || item?.revoked_at || item?.usage_released_at || item?.updated_at || item?.expires_at;
  }
  return item?.completed_at || item?.usage_used_at || item?.revoked_at || item?.usage_released_at || item?.expires_at;
}
